import React, { useEffect, useState, useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { ConfluenceLogo } from '@atlaskit/logo';
import Modal, { type KeyboardOrMouseEvent } from '@atlaskit/modal-dialog';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import CreditCardIcon from '@atlaskit/icon/glyph/creditcard';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import PageIcon from '@atlaskit/icon/glyph/page';
import { N500, N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/standard-button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { type DialogsContainerProps } from '@confluence/dialogs';
import { h300Compiled } from '@confluence/typography-placeholder';

import { LearnMoreButton } from '../buttons/LearnMoreButton';
import { ConditionalUserButtons } from '../buttons/ConditionalUserButtons';
import CellphonesTall from '../assets/CellphonesTall.png';
import CollaborationDesktop from '../assets/CollaborationDesktop.png';
import { ANALYTICS_SOURCE, type UserTypes } from '../constants';

import { AtlassianIntelligenceIcon } from './AtlassianIntelligenceIcon';
import { WhiteboardsIcon } from './WhiteboardsIcon';

export interface LearnMoreModalProps extends DialogsContainerProps {
	cloudId: string;
	contentId: string;
	userType: UserTypes;
	openTrigger?: 'click';
}

export const LEARN_MORE_MODAL_ACTION_SUBJECT_ID = 'PLPoweredByConfluenceModal';
export const RENDER_LOCATION = LEARN_MORE_MODAL_ACTION_SUBJECT_ID;
const FADE_IN_CLASS = 'image-fade-in-onload';

export const i18n = defineMessages({
	poweredByLogoTagline: {
		id: 'external-share.powered-by-confluence.learn-more-modal.logo-tagline',
		defaultMessage: 'POWERED BY',
		description: 'The tagline next to the Confluence logo in the Learn More modal',
	},
	poweredByConfluenceLearnMoreModalBenefitOne: {
		id: 'external-share.powered-by-confluence.learn-more-modal.benefit-one',
		defaultMessage: 'Free for up to 10 users',
		description: 'The first benefit row of the Learn More modal.',
	},
	poweredByConfluenceLearnMoreModalBenefitTwo: {
		id: 'external-share.powered-by-confluence.learn-more-modal.benefit-two',
		defaultMessage: 'No credit card needed',
		description: 'The second benefit row of the Learn More modal.',
	},
	poweredByConfluenceLearnMoreModalBenefitThree: {
		id: 'external-share.powered-by-confluence.learn-more-modal.benefit-three',
		defaultMessage: 'Real-time collaboration',
		description: 'The third benefit row of the Learn More modal.',
	},
	poweredByConfluenceLearnMoreModalBenefitFour: {
		id: 'external-share.powered-by-confluence.learn-more-modal.benefit-four',
		defaultMessage: '75+ best practice templates',
		description: 'The fourth benefit row of the Learn More modal.',
	},
	poweredByConfluenceLearnMoreModalBenefitFive: {
		id: 'external-share.powered-by-confluence.learn-more-modal.benefit-five',
		defaultMessage: 'Whiteboards',
		description: 'The fifth benefit row of the Learn More modal.',
	},
	poweredByConfluenceLearnMoreModalBenefitSix: {
		id: 'external-share.powered-by-confluence.learn-more-modal.benefit-six',
		defaultMessage: 'Atlassian Intelligence',
		description: 'The sixth benefit row of the Learn More modal.',
	},
	poweredByConfluenceLearnMoreModalCloseButtonLabel: {
		id: 'external-share.powered-by-confluence.learn-more-modal.close-button-label',
		defaultMessage: 'Close modal',
		description: 'Close button label',
	},
	poweredByConfluenceLearnMoreModalBackgroundImageAltTag: {
		id: 'external-share.powered-by-confluence.learn-more-modal.background-image-alt-tag',
		defaultMessage: 'Background',
		description: "HTML alt tag description for the modal's background image",
	},
});

const benefitsList = [
	{
		glyph: CheckCircleOutlineIcon,
		message: i18n.poweredByConfluenceLearnMoreModalBenefitOne,
	},
	{
		glyph: CreditCardIcon,
		message: i18n.poweredByConfluenceLearnMoreModalBenefitTwo,
	},
	{
		glyph: CommentIcon,
		message: i18n.poweredByConfluenceLearnMoreModalBenefitThree,
	},
	{
		glyph: PageIcon,
		message: i18n.poweredByConfluenceLearnMoreModalBenefitFour,
	},
	{
		glyph: WhiteboardsIcon,
		message: i18n.poweredByConfluenceLearnMoreModalBenefitFive,
	},
	{
		glyph: AtlassianIntelligenceIcon,
		message: i18n.poweredByConfluenceLearnMoreModalBenefitSix,
	},
];

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonWrapper = styled.div({
	position: 'absolute',
	right: token('space.200', '16px'),
	top: token('space.200', '16px'),
	zIndex: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		height: token('space.300', '24px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PoweredByHeading = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...h300Compiled,
	marginTop: token('space.0', '0px'),
	marginBottom: token('space.050', '4px'),
	color: token('color.text.subtle', N500),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PoweredByConfluenceLogoContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalValuePropSection = styled.section({
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	flexWrap: 'nowrap',
	padding: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalButtonGroup = styled.div({
	display: 'flex',
	justifyContent: 'flex-start',
	gap: token('space.100', '8px'),
	marginTop: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BenefitsListItem = styled.li({
	padding: token('space.0', '0px'),
	marginTop: token('space.0', '0px'),
	marginLeft: token('space.0', '0px'),
	marginBottom: token('space.100', '8px'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	gap: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BenefitsContainer = styled.ul({
	margin: token('space.0', '0px'),
	padding: token('space.0', '0px'),
});

const BenefitsList = () => (
	<BenefitsContainer>
		{benefitsList.map(({ glyph: GlyphIcon, message }) => (
			<BenefitsListItem key={message.id}>
				<GlyphIcon primaryColor={token('color.icon', N500)} size="medium" label="" />
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
				<span style={{ color: token('color.text', N800) }}>
					<FormattedMessage {...message} />
				</span>
			</BenefitsListItem>
		))}
	</BenefitsContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LearnMoreButtonWrapper = styled.div({
	display: 'flex',
	marginTop: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> a': {
		maxWidth: '100px',
		lineHeight: token('space.250', '20px'),
		height: token('space.250', '20px'),
		maxHeight: token('space.250', '20px'),
		padding: token('space.0', '0px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalImageSection = styled.section({
	width: '100%',
	display: 'flex',
	boxSizing: 'border-box',
	marginTop: 'auto',
	paddingLeft: token('space.400', '32px'),
	paddingRight: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> img': {
		width: '100%',
		height: 'auto',

		// image fade-in animation base properties
		opacity: 0,
		transform: `translateY(${token('space.200', '16px')})`,
		transition: 'opacity 0.8s, transform 0.8s',

		// image fade-in animation class
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		[`&.${FADE_IN_CLASS}`]: {
			opacity: 1,
			transform: 'translateY(0)',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalContent = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexWrap: 'nowrap',
	flexGrow: 1,
	position: 'relative',
	'@media (min-width: 560px)': {
		flexDirection: 'row',
		borderRadius: token('border.radius.100', '3px'),
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.pl-value-prop-section': {
			flex: '2 40%',
			maxWidth: '320px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.pl-cta-button-group': {
			marginTop: 'auto',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.pl-image-section': {
			flex: '3 60%',
			maxWidth: '480px',
			padding: token('space.0', '0px'),
			position: 'relative',
			height: '516px',
			overflow: 'hidden',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'> img': {
				width: 'auto',
				height: '100%',
				minWidth: '100%',
				position: 'absolute',
				top: token('space.0', '0px'),
				right: token('space.0', '0px'),
				bottom: token('space.0', '0px'),

				// for desktop only fade-in opacity
				transition: 'opacity 0.8s',
			},
		},
	},
	'@media (min-width: 800px)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.pl-value-prop-section': {
			padding: token('space.500', '40px'),
		},
	},
});

const determineModalCloseTrigger = (
	event: KeyboardOrMouseEvent,
	analyticsEvent: UIAnalyticsEvent,
): string => {
	return analyticsEvent.payload.actionSubject === 'button'
		? 'closeButton'
		: analyticsEvent.payload.actionSubject === 'modalDialog'
			? event instanceof KeyboardEvent && event.key === 'Escape'
				? 'escapeKey'
				: 'outsideClick'
			: 'unknown';
};

const determineHeroImage = () =>
	window?.innerWidth >= 560 ? CollaborationDesktop : CellphonesTall;

const LearnMoreModalInternal = ({
	onClose,
	onCloseComplete,
	cloudId,
	contentId,
	userType,
	openTrigger = 'click',
}: LearnMoreModalProps) => {
	const { formatMessage } = useIntl();
	const initialHeroImage = useMemo(determineHeroImage, []);
	const [heroImageSource, setHeroImageSource] = useState(initialHeroImage);
	const [imageFadeInClass, setImageFadeInClass] = useState('');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fadeImageIn = () => setImageFadeInClass('image-fade-in-onload');

	useEffect(() => {
		const swapImage = () => setHeroImageSource(determineHeroImage());
		window.addEventListener('resize', swapImage);
		return () => window.removeEventListener('resize', swapImage);
	}, []);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'shown',
				actionSubject: 'modalDialog',
				actionSubjectId: LEARN_MORE_MODAL_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				attributes: {
					openTrigger,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, openTrigger]);

	const closeModalWithAnalytics = (
		event: KeyboardOrMouseEvent,
		analyticsEvent: UIAnalyticsEvent,
	): void => {
		onClose();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'closed',
				actionSubject: 'modalDialog',
				actionSubjectId: LEARN_MORE_MODAL_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				attributes: {
					closeTrigger: determineModalCloseTrigger(event, analyticsEvent),
				},
			},
		}).fire();
	};

	return (
		<Modal
			width={800}
			onClose={closeModalWithAnalytics}
			onCloseComplete={onCloseComplete}
			shouldScrollInViewport
			shouldCloseOnEscapePress
			shouldCloseOnOverlayClick
		>
			<ModalContent>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<ModalValuePropSection className="pl-value-prop-section">
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<CloseButtonWrapper className="pl-close-button-container">
						<Button
							appearance="link"
							onClick={closeModalWithAnalytics}
							iconBefore={
								<CrossIcon
									size="small"
									label={formatMessage(i18n.poweredByConfluenceLearnMoreModalCloseButtonLabel)}
									primaryColor={token('color.text.subtle', N500)}
								/>
							}
						/>
					</CloseButtonWrapper>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<PoweredByConfluenceLogoContainer className="logo-container">
						<PoweredByHeading>POWERED BY</PoweredByHeading>
						<ConfluenceLogo appearance="brand" />
					</PoweredByConfluenceLogoContainer>
					<BenefitsList />
					<LearnMoreButtonWrapper>
						<LearnMoreButton contentId={contentId} renderLocation={RENDER_LOCATION} />
					</LearnMoreButtonWrapper>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<ModalButtonGroup className="pl-cta-button-group">
						<ConditionalUserButtons
							userType={userType}
							contentId={contentId}
							cloudId={cloudId}
							renderLocation={RENDER_LOCATION}
							reverseButtonOrder
							shortenTryButton
						/>
					</ModalButtonGroup>
				</ModalValuePropSection>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<ModalImageSection className="pl-image-section">
					<img
						src={heroImageSource}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={imageFadeInClass}
						onLoad={fadeImageIn}
						alt={formatMessage(i18n.poweredByConfluenceLearnMoreModalBackgroundImageAltTag)}
					/>
				</ModalImageSection>
			</ModalContent>
		</Modal>
	);
};

export const LearnMoreModal = (props: LearnMoreModalProps) => (
	<TransparentErrorBoundary attribution={Attribution.EXTERNAL_COLLAB}>
		<LearnMoreModalInternal {...props} />
	</TransparentErrorBoundary>
);
